import { GUNDI_OBJECT_SPECS } from './gundiObjectSpecs';

export type ValidationError = {
  field: string;
  messageKey: string;
  messageParams?: Record<string, string>;
  severity: 'error' | 'warning';
};

const EXPECTED_TYPE_MESSAGE_KEY = 'Expected {{expected}}, got {{actual}}';

function getActualTypeName(value: any): string {
  if (Array.isArray(value)) return 'array';
  if (value === null) return 'null';
  if (typeof value === 'number' && Number.isNaN(value)) return 'NaN';
  return typeof value;
}

function isTypeMatch(value: any, expectedType: string): boolean {
  switch (expectedType) {
    case 'string':
      return typeof value === 'string';
    // NaN (e.g. "To number" applied to a non-numeric value) must not pass as a valid
    // number — the corresponding jq would error at runtime.
    case 'number':
      return typeof value === 'number' && !Number.isNaN(value);
    case 'object':
      return typeof value === 'object' && !Array.isArray(value);
    default:
      return true;
  }
}

/** Returns true if the value is a complex expression placeholder (e.g. `<.alertType>` or `<(if ... end)>`). */
function isComplexPlaceholder(value: any): boolean {
  return (
    typeof value === 'string' && value.startsWith('<') && value.endsWith('>')
  );
}

/** Validates a mapped output object against the Gundi object spec for the given type. */
export function validateMappedOutput(
  output: any,
  objectType: string,
): ValidationError[] {
  const spec = GUNDI_OBJECT_SPECS[objectType];
  if (!spec || !output || typeof output !== 'object') return [];

  const errors: ValidationError[] = [];

  for (const field of spec.fields) {
    const value = getNestedValue(output, field.key);

    if (field.required && (value === undefined || value === null)) {
      errors.push({
        field: field.key,
        messageKey: 'Missing required field',
        severity: 'error',
      });
      continue;
    }

    if (value === undefined || value === null) continue;

    // Skip type validation for complex expression placeholders — their actual
    // type will be determined at runtime by jq, not by the builder preview
    if (isComplexPlaceholder(value)) continue;

    if (field.type && !isTypeMatch(value, field.type)) {
      errors.push({
        field: field.key,
        messageKey: EXPECTED_TYPE_MESSAGE_KEY,
        messageParams: {
          expected: field.type,
          actual: getActualTypeName(value),
        },
        severity: 'warning',
      });
    }
  }

  return errors;
}

/** Scans a jq filter string for known field names and returns the set of mapped fields. */
export function extractMappedFields(jqFilter: string): Set<string> {
  const mapped = new Set<string>();
  if (!jqFilter.trim()) return mapped;

  const FIELD_PATTERNS: [RegExp, string][] = [
    [/\bsource\s*:/, 'source'],
    [/\brecorded_at\s*:/, 'recorded_at'],
    [/\btitle\s*:/, 'title'],
    [/\bevent_type\s*:/, 'event_type'],
    [/\bsubject_type\s*:/, 'subject_type'],
    [/\bsource_name\s*:/, 'source_name'],
    [/(?:^|[^\w])type\s*:/, 'type'],
    [/\blocation\s*:\s*\{[^}]*\blat\s*:/, 'location.lat'],
    [/\blocation\s*:\s*\{[^}]*\blon\s*:/, 'location.lon'],
    [/\badditional\s*:|\["additional"\]\s*:/, 'additional'],
    [/\bevent_details\s*:|\["event_details"\]\s*:/, 'event_details'],
    [/\bannotations\s*:|\["annotations"\]\s*:/, 'annotations'],
    [/\bgeometry\s*:|\["geometry"\]\s*:/, 'geometry'],
    [/\bstatus\s*:/, 'status'],
  ];

  for (const [pattern, fieldName] of FIELD_PATTERNS) {
    if (pattern.test(jqFilter)) mapped.add(fieldName);
  }

  return mapped;
}

function getNestedValue(obj: any, path: string): any {
  let current = obj;
  for (const part of path.split('.')) {
    if (current == null) return undefined;
    current = current[part];
  }
  return current;
}
